import type { CdpWebSdkConfig } from "webapp";
import { fetchConfig, getFeatureFlagsFromCache } from "../hooks/use-config";
import type { CdcUserAttributes } from "../models/cdcUser";
import { getDataFromLocalstorageByKey } from "./utils/get-data-localstorage";

const CDP_SDK_URL = "https://cdns.cdp.gigya.com/websdk.js";

type CdpSdk = {
  report: (eventName: string, payload?: Record<string, unknown>) => void;
};

type GigyaCdp = {
  init: (options: CdpWebSdkConfig) => Promise<CdpSdk>;
};

// The `gigya` global is typed elsewhere (@gigya-ts/web-sdk); use a narrow
// cast here instead of augmenting Window and colliding with that type.
const getGigyaCdp = (): GigyaCdp | undefined =>
  (window as unknown as { gigya?: { cdp?: GigyaCdp } }).gigya?.cdp;

let initPromise: Promise<CdpSdk | null> | null = null;

const injectCdpScript = (): Promise<void> =>
  new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${CDP_SDK_URL}"]`)) {
      resolve();
      return;
    }
    const tag = document.createElement("script");
    tag.src = CDP_SDK_URL;
    tag.async = true;
    tag.onload = () => resolve();
    tag.onerror = () => reject(new Error("CDP WebSDK failed to load"));
    document.head.appendChild(tag);
  });

const initCdp = async (): Promise<CdpSdk | null> => {
  const config = await fetchConfig();
  if (!config.cdpWebSdk) return null;

  await injectCdpScript();
  const cdp = getGigyaCdp();
  if (!cdp?.init) {
    throw new Error("gigya.cdp not available after loading CDP WebSDK");
  }
  return await cdp.init(config.cdpWebSdk);
};

const getCdp = (): Promise<CdpSdk | null> => {
  if (!initPromise) {
    initPromise = initCdp().catch((error) => {
      console.warn("CDP init failed", error);
      initPromise = null;
      return null;
    });
  }
  return initPromise;
};

/**
 * Payload for `CDP.report('Imagio CF Web Behaviour', ...)`. Only the fields
 * we actively populate are typed; the CDP schema has more optional fields.
 */
export interface ImagioCfWebBehaviourPayload {
  cov_web_act_type?: string;
  cov_web_act_date?: string;
  cov_page_url?: string;
  cov_query_param?: string;
  cov_primary_attribute?: string;
  cov_campaign_id?: string;
  cov_material?: string;
  cov_color_code?: string;
  cov_uvp_number?: string;
  primaryEmail?: string;
}

/**
 * Fire an `Imagio CF Web Behaviour` "interesting moment" report to Gigya CDP.
 * No-ops when the client config has no `cdpWebSdk` section, when the SDK
 * fails to load, or when `CDP.report` is unavailable. Failures are logged
 * and swallowed — this must never block the caller.
 */
export const reportCdpImagioCfWebBehaviour = async (
  payload: ImagioCfWebBehaviourPayload,
): Promise<void> => {
  if (!getFeatureFlagsFromCache().cdpLeadCreation) return;
  try {
    const sdk = await getCdp();
    if (!sdk) return;
    const config = await fetchConfig();
    const cdcUser: CdcUserAttributes | undefined =
      getDataFromLocalstorageByKey("cdcUser");
    sdk.report("Imagio CF Web Behaviour", {
      cov_web_act_date: new Date().toISOString(),
      cov_page_url: window.location.href,
      cov_query_param: window.location.search || undefined,
      cov_campaign_id: config.cdpWebSdk?.campaignId,
      primaryEmail: cdcUser?.email,
      ...payload,
    });
  } catch (error) {
    console.warn("CDP report failed", error);
  }
};
